import React from "react"
import useDeliveryEntitiesPage from "../Dashboarding.logic/useDeliveryEntitiesPage"
import SimpleTable from "../Dashboarding.components/SimpleTable"
import RETENTION_METRICS_DEFINITIONS from "../Dashboarding.constants/RETENTION_METRICS_DEFINITIONS.js"
import "./DeliveryEntitiesView.css"

const DeliveryEntitiesView = () => {
  const { deliveryEntitiesData, topProducts, topBranches, globalMetrics, filters, updateFilter, isLoading, error } = useDeliveryEntitiesPage()
  const [showAdvancedMetrics, setShowAdvancedMetrics] = React.useState(false)

  // Fonction pour obtenir le symbole de devise
  const getCurrencySymbol = () => {
    if (filters.currency === 'ALL') return '£' // Default
    return filters.currency
  }

  // Fonction pour créer une metric card avec tooltip
  const createMetricCard = (metricKey, value, customSuffix = '') => {
    const definition = RETENTION_METRICS_DEFINITIONS[metricKey]
    if (!definition) return null

    const displayValue = typeof value === 'number' && value % 1 !== 0 
      ? value.toFixed(2) 
      : value?.toLocaleString() || '0'

    const suffix = customSuffix || ''

    return (
      <div 
        className="metric-card tooltip-container" 
        key={metricKey}
        title={`${definition.description}\n\nFormule: ${definition.formula}`}
      >
        <div className="metric-value">{displayValue}{suffix}</div>
        <div className="metric-label">{definition.title}</div>
        <div className="metric-tooltip">
          <div className="tooltip-content">
            <strong>{definition.title}</strong>
            <p>{definition.description}</p>
            <div className="tooltip-calculation">
              <em>Formule: {definition.formula}</em>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Calculer les métriques de résumé améliorées
  const summaryMetrics = React.useMemo(() => {
    // Métriques de base
    const totalEntities = deliveryEntitiesData.length
    const totalPairs = deliveryEntitiesData.reduce((sum, entity) => sum + entity.totalPairs, 0)
    const totalSales = deliveryEntitiesData.reduce((sum, entity) => sum + entity.totalSales, 0)
    
    // Métriques business plus pertinentes
    const avgSalesPerEntity = totalEntities > 0 ? totalSales / totalEntities : 0
    const avgPairsPerEntity = totalEntities > 0 ? totalPairs / totalEntities : 0
    
    // Métriques de performance
    const topEntitySales = Math.max(...deliveryEntitiesData.map(e => e.totalSales), 0)
    const entitySalesRange = topEntitySales - Math.min(...deliveryEntitiesData.map(e => e.totalSales), 0)
    
    // Clarification : entités avec commandes multiples vs one-shot uniquement
    const multiOrderEntities = deliveryEntitiesData.filter(e => e.totalOrders > 1).length
    const multiOrderRate = totalEntities > 0 ? (multiOrderEntities / totalEntities) * 100 : 0
    
    return { 
      totalEntities, 
      totalPairs, 
      totalSales, 
      avgSalesPerEntity,
      avgPairsPerEntity,
      topEntitySales,
      multiOrderRate,
      multiOrderEntities,
      entitySalesRange
    }
  }, [deliveryEntitiesData])

  // Fonction pour créer une metric card avec tooltip personnalisé
  const createSummaryCard = (value, label, tooltip, suffix = '') => {
    return (
      <div 
        className="metric-card tooltip-container" 
        title={tooltip}
      >
        <div className="metric-value">{value}{suffix}</div>
        <div className="metric-label">{label}</div>
        <div className="metric-tooltip">
          <div className="tooltip-content">
            <strong>{label}</strong>
            <p>{tooltip}</p>
          </div>
        </div>
      </div>
    )
  }

  // Fonction pour créer des barres de progression
  const createProgressBar = (value, maxValue, colorClass = 'sales') => {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0
    return (
      <div className={`progress-bar-container ${colorClass}`}>
        <div
          className={`progress-bar ${colorClass}`}
          style={{ width: `${Math.max(percentage, 3)}%` }}
        />
      </div>
    )
  }

  const maxValues = React.useMemo(() => {
    return {
      maxDeliveryEntitySales: Math.max(...deliveryEntitiesData.map(e => e.totalSales), 1),
      maxDeliveryEntityPairs: Math.max(...deliveryEntitiesData.map(e => e.totalPairs), 1),
      maxProductSales: Math.max(...topProducts.map(p => p.sales), 1),
      maxProductPairs: Math.max(...topProducts.map(p => p.pairs), 1),
      maxBranchSales: Math.max(...topBranches.map(b => b.sales), 1),
      maxBranchPairs: Math.max(...topBranches.map(b => b.pairs), 1),
    }
  }, [deliveryEntitiesData, topProducts, topBranches])

  // Affichage d'erreur
  if (error) {
    return (
      <div className="delivery-entities-container">
        <div className="error-state">
          <div className="error-message">
            <h3>Erreur de chargement</h3>
            <p>{error}</p>
            <button
              type="button"
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Affichage de chargement
  if (isLoading) {
    return (
      <div className="delivery-entities-container">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Chargement des données...</p>
        </div>
      </div>
    )
  }

  const deliveryEntitiesColumns = [
    {
      key: 'name',
      label: 'Delivery Entity',
      width: 200,
      render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>,
    },
    {
      key: 'totalPairs',
      label: 'Pairs',
      width: 90,
      render: (value) => (
        <div className="bar-cell">
          {createProgressBar(value, maxValues.maxDeliveryEntityPairs, 'pairs')}
          <span className="bar-text">{value?.toLocaleString() || '0'}</span>
        </div>
      ),
    },
    {
      key: 'totalSales',
      label: 'Sales',
      width: 110,
      render: (value) => (
        <div className="bar-cell">
          {createProgressBar(value, maxValues.maxDeliveryEntitySales, 'sales')}
          <span className="bar-text">
            {getCurrencySymbol()}
            {value?.toLocaleString() || '0'}
          </span>
        </div>
      ),
    },
    {
      key: 'branches',
      label: 'Branches',
      width: 90,
      render: (value) => <span>{value || 0}</span>,
    },
    {
      key: 'lastOrderFormatted',
      label: 'Last Order',
      width: 100,
      render: (value) => <span>{value || 'N/A'}</span>,
    },
    {
      key: 'noReorder',
      label: 'No Re-order',
      width: 90,
      render: (value) => <span>{value || 0}</span>,
    },
  ]

  const productsColumns = [
    {
      key: 'name',
      label: 'Product',
      width: 180,
      render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>,
    },
    {
      key: 'pairs',
      label: 'Pairs',
      width: 80,
      render: (value) => (
        <div className="bar-cell">
          {createProgressBar(value, maxValues.maxProductPairs, 'pairs')}
          <span className="bar-text">{value?.toLocaleString() || '0'}</span>
        </div>
      ),
    },
    {
      key: 'sales',
      label: 'Sales',
      width: 100,
      render: (value) => (
        <div className="bar-cell">
          {createProgressBar(value, maxValues.maxProductSales, 'sales')}
          <span className="bar-text">
            {getCurrencySymbol()}
            {value?.toLocaleString() || '0'}
          </span>
        </div>
      ),
    },
  ]

  const branchesColumns = [
    {
      key: 'name',
      label: 'Branch',
      width: 180,
      render: (value) => <span style={{ fontWeight: 500 }}>{value}</span>,
    },
    {
      key: 'pairs',
      label: 'Pairs',
      width: 80,
      render: (value) => (
        <div className="bar-cell">
          {createProgressBar(value, maxValues.maxBranchPairs, 'pairs')}
          <span className="bar-text">{value?.toLocaleString() || '0'}</span>
        </div>
      ),
    },
    {
      key: 'sales',
      label: 'Sales',
      width: 100,
      render: (value) => (
        <div className="bar-cell">
          {createProgressBar(value, maxValues.maxBranchSales, 'sales')}
          <span className="bar-text">
            {getCurrencySymbol()}
            {value?.toLocaleString() || '0'}
          </span>
        </div>
      ),
    },
    {
      key: 'lastOrderFormatted',
      label: 'Last Order',
      width: 100,
      render: (value) => <span>{value || 'N/A'}</span>,
    },
  ]

  return (
    <div className="delivery-entities-container">
      {/* KPIs business améliorés avec tooltips */}
      <div className="summary-metrics">
        {createSummaryCard(
          summaryMetrics.totalEntities, 
          "Active Entities", 
          "Nombre total d'entités de livraison ayant des commandes dans la période filtrée"
        )}
        {createSummaryCard(
          `${getCurrencySymbol()}${Math.round(summaryMetrics.avgSalesPerEntity).toLocaleString()}`, 
          "Avg Sales/Entity", 
          "Chiffre d'affaires moyen par entité de livraison (Total Sales / Nombre d'entités)"
        )}
        {createSummaryCard(
          summaryMetrics.multiOrderRate.toFixed(1), 
          "Multi-order Rate", 
          `Pourcentage d'entités ayant passé plusieurs commandes (${summaryMetrics.multiOrderEntities}/${summaryMetrics.totalEntities} entités)`,
          "%"
        )}
        {createSummaryCard(
          `${getCurrencySymbol()}${summaryMetrics.topEntitySales.toLocaleString()}`, 
          "Top Entity Sales", 
          "Chiffre d'affaires de l'entité la plus performante de la période"
        )}
      </div>

      {/* Métriques détaillées en seconde ligne avec tooltips */}
      <div className="summary-metrics secondary-metrics">
        <div className="metric-card secondary tooltip-container" title="Volume total de paires vendues sur la période">
          <div className="metric-value">{summaryMetrics.totalPairs.toLocaleString()}</div>
          <div className="metric-label">Total Pairs</div>
          <div className="metric-tooltip">
            <div className="tooltip-content">
              <strong>Total Pairs</strong>
              <p>Volume total de paires vendues sur la période</p>
            </div>
          </div>
        </div>
        <div className="metric-card secondary tooltip-container" title="Chiffre d'affaires total de toutes les entités">
          <div className="metric-value">
            {getCurrencySymbol()}
            {summaryMetrics.totalSales.toLocaleString()}
          </div>
          <div className="metric-label">Total Sales</div>
          <div className="metric-tooltip">
            <div className="tooltip-content">
              <strong>Total Sales</strong>
              <p>Chiffre d'affaires total de toutes les entités</p>
            </div>
          </div>
        </div>
        <div className="metric-card secondary tooltip-container" title="Nombre moyen de paires vendues par entité">
          <div className="metric-value">{Math.round(summaryMetrics.avgPairsPerEntity).toLocaleString()}</div>
          <div className="metric-label">Avg Pairs/Entity</div>
          <div className="metric-tooltip">
            <div className="tooltip-content">
              <strong>Avg Pairs/Entity</strong>
              <p>Nombre moyen de paires vendues par entité</p>
            </div>
          </div>
        </div>
        <div className="metric-card secondary tooltip-container" title="Écart entre l'entité la plus performante et la moins performante">
          <div className="metric-value">
            {getCurrencySymbol()}
            {Math.round(summaryMetrics.entitySalesRange).toLocaleString()}
          </div>
          <div className="metric-label">Sales Range</div>
          <div className="metric-tooltip">
            <div className="tooltip-content">
              <strong>Sales Range</strong>
              <p>Écart entre l'entité la plus performante et la moins performante</p>
            </div>
          </div>
        </div>
      </div>

      {/* Nouvelles métriques de rétention avec tooltips */}
      <div className="retention-metrics">
        {/* 7 métriques principales de l'Excel */}
        {createMetricCard('onboardedBranches', globalMetrics.onboardedBranches)}
        {createMetricCard('recurringBranches', globalMetrics.recurringBranches)}
        {createMetricCard('churnedBranches', globalMetrics.churnedBranches)}
        {createMetricCard('retentionRatio', globalMetrics.retentionRatio, '%')}
        {createMetricCard('totalRecurringOrders', globalMetrics.totalRecurringOrders)}
        {createMetricCard('avgRecurringOrdersPerBranch', globalMetrics.avgRecurringOrdersPerBranch)}
        {createMetricCard('avgSalesRecurringOrders', globalMetrics.avgSalesRecurringOrders, getCurrencySymbol())}
      </div>

      {/* Bouton discret pour métriques avancées */}
      <div className="advanced-metrics-section">
        <button 
          className="advanced-metrics-toggle-btn"
          onClick={() => setShowAdvancedMetrics(!showAdvancedMetrics)}
          title="Afficher/cacher les métriques techniques"
        >
          {showAdvancedMetrics ? '▵' : '▿'}
        </button>
        
        {showAdvancedMetrics && (
          <div className="retention-metrics advanced-metrics">
            {createMetricCard('churnRate', globalMetrics.churnRate, '%')}
            {createMetricCard('normalCycleDays', globalMetrics.normalCycleDays, ' jours')}
            {createMetricCard('gracePeriodDays', globalMetrics.gracePeriodDays, ' jours')}
          </div>
        )}
      </div>

      {/* Filtres globaux */}
      <div className="delivery-entities-filters">
        <div className="filter-group">
          <span className="filter-label">Entity:</span>
          <div className="filter-buttons">
            {['ALL', 'WGUK', 'WGE'].map(entity => (
              <button
                key={entity}
                type="button"
                className={`filter-btn ${filters.entity === entity ? 'active' : ''}`}
                onClick={() => updateFilter('entity', entity)}
              >
                {entity}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <span className="filter-label">Currency:</span>
          <div className="filter-buttons">
            {['ALL', '€', '£', '$', 'C$', 'C€'].map(currency => (
              <button
                key={currency}
                type="button"
                className={`filter-btn ${filters.currency === currency ? 'active' : ''}`}
                onClick={() => updateFilter('currency', currency)}
              >
                {currency}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <span className="filter-label">Period:</span>
          <div className="filter-buttons">
            {['FY', 'YTD'].map(period => (
              <button
                key={period}
                type="button"
                className={`filter-btn ${filters.period === period ? 'active' : ''}`}
                onClick={() => updateFilter('period', period)}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grille des tableaux */}
      <div className="delivery-entities-grid">
        <div className="delivery-entities-section">
          <h2 className="section-title">Delivery Entities</h2>
          <SimpleTable
            data={deliveryEntitiesData}
            columns={deliveryEntitiesColumns}
            maxHeight="300px"
          />
        </div>

        <div className="delivery-entities-section">
          <h2 className="section-title">Top 20 branches</h2>
          <SimpleTable
            data={topBranches}
            columns={branchesColumns}
            maxHeight="300px"
          />
        </div>

        <div className="delivery-entities-section">
          <h2 className="section-title">Top 20 Products</h2>
          <SimpleTable
            data={topProducts}
            columns={productsColumns}
            maxHeight="300px"
          />
        </div>
      </div>
    </div>
  )
}

export default DeliveryEntitiesView 